import { Component, EventEmitter, Output, ElementRef, QueryList, ViewChildren, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { CommonModule } from '@angular/common';
import { Input } from '@angular/core';
import { MatIcon } from "@angular/material/icon";

interface Card {
  id: number | string;
  type: string; // e.g., 'visa'
  maskedNumber?: string;
  bankName?: string;
  isNew?: boolean;
}

@Component({
  selector: 'app-card-details',
  standalone: true,
  imports: [MatIcon, CommonModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatRadioModule],
  templateUrl: './card-details.component.html',
  styleUrls: ['./card-details.component.css']
})
export class CardDetailsComponent implements AfterViewInit {
  @Input() nameOnCard!: FormControl;
  @Input() accountnumber!: FormControl;
  @Input() expirationDate!: FormControl;
  @Input() securityCode!: FormControl;
  @Output() cardDetailsUpdated = new EventEmitter<{
    accountnumber: string;
    accountnumber_amex: string;
    cardsecuritycode: string;
    cardsecuritycode_amex: string;
    cardtype: string;
    expirationmonthmm: string;
    expirationyearyyyy: string;
  }>();

  selectedCard: number | string | null = 'new';
  cardTypeImage: string = '';
  securityPlaceholder: string = 'CVV/CVC/CID';
  securityMaxLength: number = 3;

  cardImages: { [key: string]: string } = {
    Visa: '/assets/icons/Visa.svg',
    Mastercard: '/assets/icons/Mastercard.svg',
    amex: '/assets/icons/AMEX.svg',
    Discover: 'assets/icons/Discover.svg',
    Unknown: '/assets/icons/Vssisa.svg'
  };

  cards: Card[] = [
    { id: 'new', type: 'new', bankName: 'New card', isNew: true }
  ];

  cardForm = new FormGroup({
    nameOnCard: new FormControl('', Validators.required),
    cardNumber: new FormControl('', [Validators.required]), // Dummy card number
    expirationDate: new FormControl('', [Validators.pattern(/^(0[1-9]|1[0-2])\/(2[4-9]|[3-9][0-9])$/)]), // Dummy expiration date
    securityCode: new FormControl('', Validators.required)
  });


  @ViewChildren('cardOption') cardOptions!: QueryList<ElementRef>;

  constructor(private cdr: ChangeDetectorRef) { }

  ngAfterViewInit() {
    this.selectCard('new');
    this.cdr.detectChanges();
  }

  get cardNumberControl(): FormControl {
    return this.cardForm.get('cardNumber') as FormControl;
  }
  get expirationDateControl(): FormControl {
    return this.cardForm.get('expirationDate') as FormControl;

  }

  onExpirationDateChange(): void {
    const expirationDate = this.expirationDateControl.value || '';
    let expirationMonth = '';
    let expirationYear = '';

    if (expirationDate.includes('/')) {
      const [month, year] = expirationDate.split('/');
      expirationMonth = month.padStart(2, '0');
      expirationYear = year.startsWith('20') ? year : `20${year}`;
    }

    this.cardDetailsUpdated.emit({
      accountnumber: '',
      accountnumber_amex: '',
      cardsecuritycode: '',
      cardsecuritycode_amex: '',
      cardtype: '',
      expirationmonthmm: expirationMonth,
      expirationyearyyyy: expirationYear,
    });
  }


  onCardDetailsChange(): void {
    const cardType = this.getCardType(this.cardForm.get('cardNumber')?.value || '');

    this.cardDetailsUpdated.emit({
      accountnumber: this.cardForm.get('cardNumber')?.value || '',
      cardsecuritycode: this.cardForm.get('securityCode')?.value || '',
      cardtype: cardType,
      expirationmonthmm: '',
      expirationyearyyyy: '',
      accountnumber_amex: this.cardForm.get('cardNumber')?.value || '',
      cardsecuritycode_amex: this.cardForm.get('securityCode')?.value || '',
    });
  }
  updateCardDetails(): void {
    const cardDetails = {
      accountnumber: this.cardForm.get('cardNumber')?.value || '',
      accountnumber_amex: this.cardForm.get('cardNumber')?.value || '',
      cardsecuritycode: this.cardForm.get('securityCode')?.value || '',
      cardsecuritycode_amex: this.cardForm.get('securityCode')?.value || '',
      cardtype: this.getCardType(this.cardForm.get('cardNumber')?.value || ''),
      expirationmonthmm: this.cardForm.get('expirationDate')?.value?.split('/')[0] || '',
      expirationyearyyyy: this.cardForm.get('expirationDate')?.value?.split('/')[1] || '',
    };
    this.cardDetailsUpdated.emit(cardDetails);
  }
  selectCard(cardId: number | string) {
    this.selectedCard = cardId;
    setTimeout(() => {
      const selectedCardElement = this.cardOptions.find((card) => card.nativeElement.id === `card-${cardId}`);
      selectedCardElement?.nativeElement.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      this.cdr.detectChanges();
    });
  }

  isSavedCardSelected(): boolean {
    return this.selectedCard !== null && this.selectedCard !== 'new';
  }

  isCardNew(): boolean {
    return this.selectedCard === 'new';
  }

  // Card Formatting & Validation Logic
  onCardNumberInput(event: Event): void {
    const input = (event.target as HTMLInputElement);
    let cardNumber = input.value.replace(/\D/g, ''); // Remove non-numeric characters

    const cardType = this.getCardType(cardNumber);
    this.cardTypeImage = this.cardImages[cardType] || this.cardImages['Unknown'];

    // Format and set the card number
    const formattedNumber = this.formatCardNumber(cardNumber, cardType);
    this.cardNumberControl.setValue(formattedNumber, { emitEvent: false });
    this.updateSecurityCodeSettings(cardType);
    this.onCardDetailsChange();
  }

  getCardType(cardNumber: string): string {
    if (/^4/.test(cardNumber)) return 'Visa'; // Visa starts with 4
    if (/^5[1-5]/.test(cardNumber)) return 'Mastercard'; // Mastercard starts with 51-55
    if (/^3[47]/.test(cardNumber)) return 'amex'; // American Express starts with 34 or 37
    if (/^6(?:011|5|4[4-9]|22[1-9]|22[2-9][0-5])/.test(cardNumber)) return 'Discover'; // Discover
    return 'Unknown'; // Default for unrecognized types
  }


  formatCardNumber(cardNumber: string, cardType: string): string {
    let maxLength = 16;
    if (cardType === 'amex') {
      maxLength = 15;
    }
    cardNumber = cardNumber.substring(0, maxLength);
    if (cardType === 'amex') {
      return cardNumber
        .replace(/(\d{4})(\d{1,6})?(\d{1,5})?/, (match, p1, p2, p3) => {
          let formatted = p1;
          if (p2) formatted += ` ${p2}`;
          if (p3) formatted += ` ${p3}`;
          return formatted;
        });
    } else {
      return cardNumber.replace(/(\d{4})(?=\d)/g, '$1 ');
    }
  }


  onExpirationDateInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\D/g, '');
    input.value = value;

    if (value.length === 0) {
      this.expirationDateControl.setValue('');
      return;
    }

    if (value.length >= 2) {
      let month = parseInt(value.substring(0, 2), 10);
      let year = parseInt(value.substring(2), 10); // Parse full year string

      if (month < 1 || month > 12) {
        month = 1; // Default to January if invalid
      }

      const formattedMonth = month.toString().padStart(2, '0');

      // Get the current year and month
      const currentDate = new Date();
      const currentYear = parseInt(currentDate.getFullYear().toString().slice(-2), 10); // Last two digits of the year
      const currentMonth = currentDate.getMonth() + 1; // Months are zero-indexed

      // Check if the year or month is invalid
      if (
        value.length >= 4 &&
        (year < currentYear || (year === currentYear && month <= currentMonth))
      ) {
        input.value = '';
        input.blur();
        this.cardDetailsUpdated.emit({
          accountnumber: this.cardForm.get('cardNumber')?.value || '',
          accountnumber_amex: this.cardForm.get('cardNumber')?.value || '',
          cardsecuritycode: this.cardForm.get('securityCode')?.value || '',
          cardsecuritycode_amex: this.cardForm.get('securityCode')?.value || '',
          cardtype: this.getCardType(this.cardForm.get('cardNumber')?.value || ''),
          expirationmonthmm: '',
          expirationyearyyyy: '',
        });
        input.focus();
        return;
      }

      if (value.length > 2) {
        input.value = `${formattedMonth}/${year}`;
        this.expirationDateControl.setValue(`${formattedMonth}/${year}`, { emitEvent: false });

        // Emit formatted month and year
        this.cardDetailsUpdated.emit({
          accountnumber: this.cardForm.get('cardNumber')?.value || '',
          accountnumber_amex: this.cardForm.get('cardNumber')?.value || '',
          cardsecuritycode: this.cardForm.get('securityCode')?.value || '',
          cardsecuritycode_amex: this.cardForm.get('securityCode')?.value || '',
          cardtype: this.getCardType(this.cardForm.get('cardNumber')?.value || ''),
          expirationmonthmm: formattedMonth,
          expirationyearyyyy: `20${year}`,
        });
      } else {
        input.value = formattedMonth;
        this.expirationDateControl.setValue(formattedMonth, { emitEvent: false });

        // Emit partial data for month
        this.cardDetailsUpdated.emit({
          accountnumber: this.cardForm.get('cardNumber')?.value || '',
          accountnumber_amex: this.cardForm.get('cardNumber')?.value || '',
          cardsecuritycode: this.cardForm.get('securityCode')?.value || '',
          cardsecuritycode_amex: this.cardForm.get('securityCode')?.value || '',
          cardtype: this.getCardType(this.cardForm.get('cardNumber')?.value || ''),
          expirationmonthmm: formattedMonth,
          expirationyearyyyy: '',
        });
      }
    } else {
      input.value = value;
      this.expirationDateControl.setValue(value, { emitEvent: false });

      // Emit partial data for the first part of the input
      this.cardDetailsUpdated.emit({
        accountnumber: this.cardForm.get('cardNumber')?.value || '',
        accountnumber_amex: this.cardForm.get('cardNumber')?.value || '',
        cardsecuritycode: this.cardForm.get('securityCode')?.value || '',
        cardsecuritycode_amex: this.cardForm.get('securityCode')?.value || '',
        cardtype: this.getCardType(this.cardForm.get('cardNumber')?.value || ''),
        expirationmonthmm: value,
        expirationyearyyyy: '',
      });
    }
    //console.log(this.expirationDateControl.value);
  }


  updateSecurityCodeSettings(cardType: string): void {
    if (cardType === 'amex') {
      this.securityPlaceholder = 'CID'; // American Express uses CID
      this.securityMaxLength = 4; // 4 digits for Amex
    } else {
      this.securityPlaceholder = 'CVV'; // Other cards use CVV
      this.securityMaxLength = 3; // 3 digits for Visa, Mastercard, Discover
    }

    // Update form control validators dynamically
    this.securityCode.setValidators([
      Validators.required,
      Validators.maxLength(this.securityMaxLength),
      Validators.pattern(`^\\d{${this.securityMaxLength}}$`),
    ]);
    this.securityCode.updateValueAndValidity();


    // Update the input field manually for the placeholder
    const securityInput = document.querySelector<HTMLInputElement>('input[formControlName="securityCode"]');
    if (securityInput) {
      securityInput.placeholder = this.securityPlaceholder;
    }
    this.onCardDetailsChange();

  }

  onInputCode(event: any) {
    const value = event.target.value;
    event.target.value = value.replace(/\D/g, '');
  }
  onInputName(event: any) {
    const value = event.target.value;
    event.target.value = value.replace(/[^a-zA-Z\s]/g, '');
  }

}
